<template>
  <div
    class="asc-fleet-fault-msg-tab-1-wrapper"
    data-automation-id="wrapper_fleet_fault_msgs_tab_1"
  >
    <div class="asc-fleet-fault-msg-tab-1-wrapper--content">
      <div class="asc-fleet-fault-msg-tab-1-wrapper--content-filter">
        <asc-chips-filter-manager
          :default-filter-options="faultMsgDefaultFilterOptions"
          @filterChanged="handleFaultMsgFiltersChange"
        ></asc-chips-filter-manager>
      </div>

      <asc-filter-legend
        :items="getLegendItems"
        :items-selected="legendItemsSelected"
        :on-item-click="handleLegendItemClick"
        tooltip="Select any status to filter below"
      ></asc-filter-legend>

      <div class="asc-fleet-fault-msg-tab-1-wrapper--content-tail">
        <asc-fault-msg-bar-chart
          :title="getChartByTailsTitle"
          :values="filteredFaultMsgCountByTailData"
          :loading="loading"
          :tooltip="getChartByTailsTooltip"
          :selected-item="getSelectedTail"
          type="faultMsg"
          @onSeriesClick="handleTailSelected"
        ></asc-fault-msg-bar-chart>
      </div>

      <div class="asc-fleet-fault-msg-tab-1-wrapper--content-chart">
        <div
          class="asc-fleet-fault-msg-tab-1-wrapper--content-chart-ataChapter"
        >
          <asc-fault-msg-bar-chart
            :title="getChartByAtaChapterTitle"
            :values="filteredFaultMsgCountByAtaChapterData"
            :loading="loading"
            :selected-item="selectedAtaChapter"
            :show-button="false"
            type="ataChapter"
            @onSeriesClick="handleAtaChapterSelected"
          ></asc-fault-msg-bar-chart>
        </div>
        <div class="asc-fleet-fault-msg-tab-1-wrapper--content-chart-para">
          <asc-fault-msg-bar-chart
            :title="getChartByParaTitle"
            :values="filteredFaultMsgCountByParaData"
            :loading="loading"
            :selected-item="selectedParas"
            :show-button="false"
            type="para"
            multiple
            :enable-drill="false"
            @onSeriesClick="handleParasSelected"
          ></asc-fault-msg-bar-chart>
        </div>
      </div>

      <div class="asc-fleet-fault-msg-tab-1-wrapper--content-table">
        <asc-fault-msg-code-grid
          :grid-columns="faultMsgCodesGridColumns"
          :title="getTableByParaTitle"
          :values="faultMsgCodesGridData"
          :message-codes="messageCodeFilters"
          :on-open-raw-report="onOpenRawReport"
          :on-visualize="onVisualize"
        ></asc-fault-msg-code-grid>
      </div>
    </div>
  </div>
</template>

<script>
import AscChipsFilterManager from '@/components/molecules/AscChipsFilterManager'
import AscFaultMsgBarChart from '@/components/molecules/AscFaultMsgBarChart'
import AscFaultMsgCodeGrid from '@/components/molecules/AscFaultMsgCodeGrid'
import AscFilterLegend from '@/components/molecules/AscFilterLegend'
import variables from '@/styles/_varsJs.scss'
import _ from 'lodash'

export default {
  name: 'AscFleetFaultMsgTab1Wrapper',

  components: {
    AscChipsFilterManager,
    AscFaultMsgBarChart,
    AscFaultMsgCodeGrid,
    AscFilterLegend,
  },

  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    faultMsgDefaultFilterOptions: {
      type: Array,
      default: () => [],
    },
    faultMsgCountByTailData: {
      type: Array,
      default: () => [],
    },
    faultMsgCountByAtaChapterData: {
      type: Array,
      default: () => [],
    },
    faultMsgCountByParaData: {
      type: Array,
      default: () => [],
    },
    faultMsgCodesGridColumns: {
      type: Array,
      default: () => [],
    },
    faultMsgCodesGridData: {
      type: Array,
      default: () => [],
    },
    onFaultMsgFiltersChange: {
      type: Function,
      default: () => {},
    },
    onFaultMsgTailChange: {
      type: Function,
      default: () => {},
    },
    onFaultMsgAtaChapterChange: {
      type: Function,
      default: () => {},
    },
    onFaultMsgParaChange: {
      type: Function,
      default: () => {},
    },
    onOpenRawReport: {
      type: Function,
      default: () => {},
    },
    onVisualize: {
      type: Function,
      default: () => {},
    },
  },

  data() {
    return {
      selectedTail: '',
      selectedAtaChapter: '',
      selectedAtaChapterId: '',
      selectedParas: [],
      legendItemsSelected: this.getLegendItemsSelectedFromLocalStorage(),
      ataChapterChartTitle: '',
      messageCodeFilters: [],
    }
  },

  computed: {
    getChartByTailsTitle() {
      return 'Fault Message Count By Tail'
    },

    getChartByTailsTooltip() {
      return 'Visualization defaults to show the top 15 tails ranked by fault message count.'
    },

    getChartByAtaChapterTitle() {
      return `Fault msg count for ${this.selectedTail} by ATA Chapter`
    },

    getChartByParaTitle() {
      return `Fault msg count for ${this.selectedTail} for ATA CH ${this.selectedAtaChapterId} by Para`
    },

    getTableByParaTitle() {
      return `Fault msg code details for ${this.selectedTail} for ATA Chapter ${this.selectedAtaChapter}`
    },

    getSelectedTail() {
      return this.selectedTail
    },

    getLegendItems() {
      return [
        {
          header: '',
          items: [
            {
              text: 'Flight Deck Effects',
              styleColor: variables.accent800,
            },
            {
              text: 'Correlated Maintenance Messages',
              styleColor: variables.accent600,
            },
            {
              text: 'NonCorrelated Maintenance Messages',
              styleColor: variables.accent300,
            },
            {
              text: 'Maintenance Memos',
              styleColor: variables.accent100,
            },
          ],
        },
      ]
    },

    filteredFaultMsgCountByTailData() {
      const data = this.getFilteredData(this.faultMsgCountByTailData)
      return data
    },

    filteredFaultMsgCountByAtaChapterData() {
      const data = this.getFilteredData(this.faultMsgCountByAtaChapterData)
      return data
    },

    filteredFaultMsgCountByParaData() {
      const data = this.getFilteredData(this.faultMsgCountByParaData)
      return data
    },
  },

  watch: {
    faultMsgCountByTailData(newVal) {
      this.faultMsgCountByTailData = newVal
      this.initTailNumber()
    },

    faultMsgCountByAtaChapterData(newVal) {
      this.faultMsgCountByAtaChapterData = newVal
      this.initAtaChapter()
    },

    faultMsgCountByParaData(newVal) {
      this.faultMsgCountByParaData = newVal
      this.initPara()
    },
  },

  mounted() {
    // load selectedTail to the first item if selectedTail is empty
    if (this.selectedTail === '') {
      const filterdTailData = this.getFilteredData(this.faultMsgCountByTailData)
      const [firstTail] = filterdTailData
      if (firstTail) {
        this.selectedTail = firstTail.name
      }
    }

    // load selectedAtaChapter to the first item if selectedAtaChapter is empty
    if (this.selectedAtaChapter === '') {
      const filteredAtaChapterData = this.getFilteredData(
        this.faultMsgCountByAtaChapterData,
      )
      const [firstAtaChapter] = filteredAtaChapterData
      if (firstAtaChapter) {
        this.selectedAtaChapter = firstAtaChapter.name
      }
    }
  },

  methods: {
    isErrorTypeSelected(errorType) {
      if (!this.legendItemsSelected) {
        return false
      }

      return this.legendItemsSelected.includes(errorType)
    },

    getFilteredData(metricData) {
      const data = metricData.map(item => {
        const updatedItem = {
          name: item.name,
          flightDeckEffects: this.isErrorTypeSelected('Flight Deck Effects')
            ? 0
            : item.flightDeckEffects,
          correlatedMaintenance: this.isErrorTypeSelected(
            'Correlated Maintenance Messages',
          )
            ? 0
            : item.correlatedMaintenance,
          nonCorrelatedMaintenance: this.isErrorTypeSelected(
            'NonCorrelated Maintenance Messages',
          )
            ? 0
            : item.nonCorrelatedMaintenance,
          maintenanceMemos: this.isErrorTypeSelected('Maintenance Memos')
            ? 0
            : item.maintenanceMemos,
        }
        updatedItem.total =
          updatedItem.flightDeckEffects +
          updatedItem.correlatedMaintenance +
          updatedItem.nonCorrelatedMaintenance +
          updatedItem.maintenanceMemos

        return updatedItem
      })

      const filteredSortedData = _.orderBy(data, ['total'], ['desc']).filter(
        item => item.total > 0,
      )

      return filteredSortedData
    },

    getLegendItemsSelectedFromLocalStorage() {
      const items = localStorage.getItem('fleet_fault_messages_legend_items')

      return items ? JSON.parse(items) : []
    },

    setLegendItemsSelectedToLocalStorage() {
      localStorage.setItem(
        'fleet_fault_messages_legend_items',
        JSON.stringify(this.legendItemsSelected),
      )
    },

    handleLegendItemClick(status) {
      this.selectedTail = null
      const isFound = this.legendItemsSelected.includes(status)
      if (isFound) {
        this.legendItemsSelected = this.legendItemsSelected.filter(
          item => item !== status,
        )
      } else {
        this.legendItemsSelected.push(status)
      }

      this.initTailNumber()

      this.setLegendItemsSelectedToLocalStorage()
    },

    handleFaultMsgFiltersChange(filters) {
      this.updateMessageCodeFilters(filters)
      if (this.onFaultMsgFiltersChange) {
        this.onFaultMsgFiltersChange(filters)
      }
    },

    handleTailSelected(tailNumber) {
      this.selectedTail = tailNumber
      if (this.onFaultMsgTailChange) {
        this.onFaultMsgTailChange(tailNumber)
      }
    },

    handleAtaChapterSelected(ataChapter) {
      const ataChapterId = ataChapter.split(' - ')[0]
      this.selectedAtaChapter = ataChapter
      this.selectedAtaChapterId = ataChapterId
      if (this.onFaultMsgAtaChapterChange) {
        this.onFaultMsgAtaChapterChange(this.selectedTail, ataChapterId)
      }
    },

    handleParasSelected(paras) {
      const availableParas = this.faultMsgCountByParaData
        .filter(item => !paras.includes(item.name))
        .map(item => item.name)
      this.selectedParas = paras
      const ataChapterId = this.selectedAtaChapter.split(' - ')[0]
      if (this.onFaultMsgParaChange) {
        this.onFaultMsgParaChange(
          this.selectedTail,
          ataChapterId,
          availableParas,
        )
      }
    },

    initTailNumber() {
      const filterdData = this.getFilteredData(this.faultMsgCountByTailData)
      const [first] = filterdData
      if (first) {
        this.handleTailSelected(first.name)
      }
    },

    initAtaChapter() {
      const filteredData = this.getFilteredData(
        this.faultMsgCountByAtaChapterData,
      )
      const [first] = filteredData
      if (first) {
        this.handleAtaChapterSelected(first.name)
      }
    },

    initPara() {
      this.handleParasSelected([], true)
    },

    updateMessageCodeFilters(filters) {
      const messageCodes = filters?.messageCodeIds || []
      this.messageCodeFilters = messageCodes.map(item => item.toString())
    },
  },
}
</script>

<style lang="scss">
.asc-fleet-fault-msg-tab-1-wrapper {
  .asc-fleet-fault-msg-tab-1-wrapper--content {
    &-filter {
      margin-bottom: 16px;
    }

    &-tail {
      margin: 16px 0;
      padding: 8px;
      border: 1px solid $gray-300;
    }
  }

  .asc-fleet-fault-msg-tab-1-wrapper--content-chart {
    display: flex;
  }

  .asc-fleet-fault-msg-tab-1-wrapper--content-chart-para {
    width: 50%;
    margin: 0 0 0 10px;
    padding: 8px;
    border: 1px solid $gray-300;
  }

  .asc-fleet-fault-msg-tab-1-wrapper--content-chart-ataChapter {
    width: 50%;
    margin: 0 10px 0 0;
    padding: 8px;
    border: 1px solid $gray-300;
  }

  .asc-fleet-fault-msg-tab-1-wrapper--content-table {
    margin-top: 16px;
    padding: 8px;
    border: 1px solid $gray-300;
  }
}
</style>
